import type { AnswerOverrides } from '@/entities/finding';
import type { AnswerRecord, MultiCountRecord, PhotoRecord } from '@/shared/offline';

import type { Answer, AnswerKey, AnswerSet } from '../engine';
import { answerKeyFromWire, multiCountKeyFromWire } from './wire-keys';

/**
 * Van Dexie-rijen (draadvorm) naar wat de engine en het scherm lezen. De
 * vertaling van (vraag, bereik) naar een engine-sleutel gaat via
 * `wire-keys.ts` - nergens anders.
 */

/**
 * De antwoordset voor de engine: antwoorden plus de aantallen van
 * multi-containers. Een antwoord draagt ook de eigen bedragen van de inspecteur;
 * de engine laat die het berekende bedrag vervangen, in de bevinding én in de
 * totalen.
 */
export function answerSetFrom(
    answers: readonly AnswerRecord[],
    counts: readonly MultiCountRecord[],
): AnswerSet {
    const set: Record<AnswerKey, Answer> = {};

    for (const record of answers) {
        set[answerKeyFromWire(record.questionId, record.multiScope)] = {
            option_ids: record.optionIds,
            value_number: record.valueNumber,
            value_text: record.valueText,
            direct_cost: record.directCost,
            term_cost: record.termCost,
            improvement_cost: record.improvementCost,
        };
    }

    for (const count of counts) {
        set[multiCountKeyFromWire(count.containerNodeId, count.multiScope)] = {
            value_number: count.count,
        };
    }

    return set;
}

export function answerRecordsByKey(
    answers: readonly AnswerRecord[],
): ReadonlyMap<AnswerKey, AnswerRecord> {
    return new Map(
        answers.map((record) => [answerKeyFromWire(record.questionId, record.multiScope), record]),
    );
}

/** De rapporttekst per vraag. */
export function notesByKey(
    records: ReadonlyMap<AnswerKey, AnswerRecord>,
): ReadonlyMap<AnswerKey, string> {
    const notes = new Map<AnswerKey, string>();

    for (const [key, record] of records) {
        if (record.reportText) {
            notes.set(key, record.reportText);
        }
    }

    return notes;
}

/** Wat de inspecteur op elk antwoord overschrijft: tekst, plek, bedragen, urgentie, kostengroep, NHG. */
export function overridesByKey(
    records: ReadonlyMap<AnswerKey, AnswerRecord>,
): ReadonlyMap<AnswerKey, AnswerOverrides> {
    const overrides = new Map<AnswerKey, AnswerOverrides>();

    for (const [key, record] of records) {
        overrides.set(key, {
            reportText: record.reportText,
            location: record.location,
            directCost: record.directCost,
            termCost: record.termCost,
            improvementCost: record.improvementCost,
            costGroupId: record.costGroupId,
            urgency: record.urgency,
            nhgClassification: record.nhgClassification,
        });
    }

    return overrides;
}

/* Een verwijderde foto is weg voor de inspecteur, ook als de server het nog moet horen. */
function visibleInOrder(photos: readonly PhotoRecord[]): PhotoRecord[] {
    return photos
        .filter((photo) => photo.deleted !== 1)
        .sort((a, b) => a.takenAt.localeCompare(b.takenAt) || a.createdAt - b.createdAt);
}

/** De foto's per vraag, in de volgorde waarin ze gemaakt zijn. */
export function photosByKey(photos: readonly PhotoRecord[]): ReadonlyMap<AnswerKey, PhotoRecord[]> {
    const map = new Map<AnswerKey, PhotoRecord[]>();

    for (const photo of visibleInOrder(photos)) {
        if (photo.findingUuid) {
            continue;
        }

        const key = answerKeyFromWire(photo.questionId, photo.multiScope);
        map.set(key, [...(map.get(key) ?? []), photo]);
    }

    return map;
}

/** De foto's per handmatige bevinding, in de volgorde waarin ze gemaakt zijn. */
export function photosByFinding(
    photos: readonly PhotoRecord[],
): ReadonlyMap<string, PhotoRecord[]> {
    const map = new Map<string, PhotoRecord[]>();

    for (const photo of visibleInOrder(photos)) {
        if (photo.findingUuid) {
            map.set(photo.findingUuid, [...(map.get(photo.findingUuid) ?? []), photo]);
        }
    }

    return map;
}
